import { compact, first, groupBy, identity, sortBy, sumBy } from "lodash";
import { action, makeAutoObservable } from "mobx";
import { AIClient } from "../../ai/AIAssistant";
import { ArchiveDocumentType } from "../../archive/commonArchiveDomain";
import {
  CompanyItem,
  CustomizedFreetextType,
  emailValidator,
  Failure,
  FreetextType,
  GetProjectDocumentSignersResponse,
  isFailure,
  isOk,
  isPending,
  Ok,
  pending,
  ProjectDocumentPreview,
  Result,
  Signer,
  SigningMethod,
  SubscriptionTypeModel,
} from "../../common/common";
import {
  CompaniesForSigning,
  createEmailField,
  DocumentsForSigning,
  LocalDateFieldModel,
  RadioFieldModel,
  SignerModel,
  SignersForm,
  StringFieldModel,
  TimeFieldModel,
} from "../../common/commonForms";
import { DecisionType } from "../../common/decisions";
import { DocumentType, getDocumentLabel } from "../../common/documents";
import {
  BatchPreviewModel,
  FileToPreview,
  toFileToPreview,
} from "../../components-v2/BatchPreviewDialog/batchPreviewDomain";
import { FilePreviewProps } from "../../components-v2/FilePreview";
import {
  DocumentSigningStatus,
  GetDocumentSigningStatusResponse,
  GetSubscriptionStatusResponse,
  SigningStatusForSigner,
  SubscriptionStatus,
} from "../../document/documentApi";
import { FileUploadConfig } from "../../fileUpload/fileUploadDomain";
import { formatDate } from "../../i18n/dateFormat";
import { DEFAULT_LANG, Language } from "../../i18n/languages";
import { TFunction, TMessage } from "../../i18n/tt";
import { showSubmitForSigningSuccess } from "../../portfolio/signatures/signaturesDomain";
import { isAlertDismissed } from "../../stores/userPrefsDomain";
import { LocalizedTextEditorModel } from "../../text/localizedTextEditorDomain";
import { CompanyRole, CompanyroleId } from "../../types/CompanyInfo";
import { getRoleTypeLabel, RoleType } from "../../types/enums/RoleType";
import { EmailStatus } from "../../types/SavedDocument";
import { ExtendedShareholderDTO } from "../../types/Shareholder";
import { User } from "../../types/UserInfo";
import { combineDateAndTime, isToday, plusDays, toDate } from "../../utils/Dates";
import { logError } from "../../utils/log";
import { boardMemberBoardMeetings, joinUrl } from "../../utils/navigation";
import { getFullName, sortedRoles } from "../../utils/Roles";
import { isBlank, isNotBlank } from "../../utils/strings";
import { resolveUrnReference } from "../../utils/UrnReference";
import {
  SignSubscriptionFormShare,
  SubscriptionModel,
} from "../../views/Pages/SubscriptionFormSign/subscriptionFormDomain";
import { supportsSelectionOfSigners } from "../boardMeetingMinutes/boardMeetingMinutesDomain";
import {
  applyDecisionAction,
  ApplyDecisionAction,
  AttachmentFollowUp,
  AttachmentType,
  AutosignDocumentEvent,
  BRSigner,
  BRSignerRole,
  CompanyRoleIdWithName,
  confirmSendToBRAction,
  ConfirmSendToBRAction,
  DbotEvent,
  DbotEventDispatcher,
  DecisionAttachment,
  DecisionItem,
  DeleteAttachmentEvent,
  DeleteAttachmentResultEvent,
  DetailsPanel,
  DocumentItem,
  DocumentViewingStatus,
  DownloadAttachmentEvent,
  DownloadDocumentAction,
  DownloadSubscriptionFormsAction,
  FollowUp,
  FollowUpStatus,
  FollowUpType,
  getDecisionAttachmentsDescription,
  getDecisionSendToBRDescription,
  GetDocumentViewingStatusResponse,
  getViewingStatusEvent,
  IncreaseOfShareCapitalSubscribersStatus,
  isDecisionApplied,
  isIdentificationAttachment,
  MeetingAuthMethod,
  OpenIncreaseOfShareCapitalSubscribersAllocationEvent,
  OpenIncreaseOfShareCapitalSubscribersStatusEvent,
  OpenReviewDialogAction,
  OpenSendForReviewDialogAction,
  OpenSigningStatusDialogAction,
  OpenSubscriptionFormBatchPreviewDialogAction,
  OpenSubscriptionStatusEvent,
  OpenViewingStatusDialogAction,
  ProjectContext,
  ProjectDocumentStatus,
  Recipient,
  RecipientId,
  RefreshSigningStatusesAction,
  requiredAttachmentLabel,
  ResendDocumentForViewing,
  resendNoticeOfBoardMeetingEvent,
  sendDocumentForSigningEvent,
  sendDocumentForViewingEvent,
  SendForReviewEvent,
  SendForReviewMessageTemplate,
  sendSubscriptionFormForSigningEvent,
  SendToBRFollowUp,
  SubscriptionFormFollowUp,
  SubscriptionFormStatusFollowUp,
  UnlockDocumentAction,
  updateBoardMeetingRecipientsEvent,
  updateCompanyRoleEmailsEvent,
  UpdateCompanyRoleItem,
  updateFollowUpStatusEvent,
  uploadAttachmentsEvent,
  UploadAttachmentsEvent,
  UploadAttachmentsResultEvent,
  ValidateProjectAction,
  ViewSubscribersFollowUp,
} from "../common/commonDomain";
import {
  filterSubscribersOnSelectedShareTypes,
  IncreaseOfShareCapitalType,
  PeriodForSubscription,
  SubscriberForIssue,
  Subscription,
  ViewSubscribersDialogModel,
} from "../increaseOfShareCapital/increaseOfShareCapitalDomain";
import { DocumentListItem } from "./SummaryController";
import { SharepointContext } from "../../sharepoint/sharepointDomain";
import { when } from "../../utils/functions";
import { next, prev } from "../../utils/arrays";

///
/// Dialogs
///

export type DialogState =
  | { type: "none" }
  | { type: "reviewSendNoticeOfGeneralMeeting"; model: ReviewSendNoticeOfGeneralMeetingDialogModel }
  | { type: "reviewSendNoticeOfBoardMeeting"; model: ReviewSendNoticeOfBoardMeetingDialogModel }
  | { type: "reviewSendSubscriptionForm"; model: ReviewSendSubscriptionFormDialogModel }
  | { type: "reviewDocumentSigning"; model: ReviewDocumentSigningDialogModel }
  | { type: "sendForReview"; model: SendForReviewDialogModel }
  | { type: "viewDocumentSigningStatus"; model: ViewDocumentSigningStatusDialogModel }
  | { type: "viewSubscriptionFormBatchPreview"; model: ViewSubscriptionFormBatchPreviewModel }
  | { type: "viewSubscriptionStatus"; model: ViewSubscriptionStatusDialogModel }
  | { type: "viewIncreaseOfShareCapitalSubscribersStatus"; model: ViewSubscribersDialogModel }
  | { type: "viewIncreaseOfShareCapitalSubscribersAllocation"; model: ViewSubscribersDialogModel }
  | { type: "viewDocumentViewingStatus"; model: ViewDocumentViewingStatusDialogModel }
  | { type: "reopenLockedDocument"; model: ReopenLockedDocumentDialogModel }
  | { type: "confirmSendToBR"; model: ConfirmSendToBRDialogModel };

export const noDialog = (): { type: "none" } => ({ type: "none" });

type ReviewSendNoticeOfGeneralMeetingDialogModelParams = {
  companyName: string;
  document: DocumentItem;
  hasInvalidCompanyRoleRecipients: boolean;
  dispatch: DbotEventDispatcher;
  closeDialog: () => void;
  subscriptionTypeModel: SubscriptionTypeModel;
  shareholderRecipients: Recipient[];
  emailBody: TMessage;
  aiClient: AIClient;
  documentStatus?: ProjectDocumentStatus;
  isMissingShareholderSSN: boolean;
  hasForeignShareholders: boolean;
};

export class ReviewSendNoticeOfGeneralMeetingDialogModel {
  readonly params: ReviewSendNoticeOfGeneralMeetingDialogModelParams;
  recipients: RecipientsEditModel;
  emailBody: LocalizedTextEditorModel;
  updateEmailResult?: Result<TMessage, TMessage>;
  isSending = false;
  authMethod: RadioFieldModel<MeetingAuthMethod | null>;
  meetingPassword: StringFieldModel;

  constructor(params: ReviewSendNoticeOfGeneralMeetingDialogModelParams) {
    this.params = params;
    this.recipients = new RecipientsEditModel(params.shareholderRecipients);
    this.emailBody = new LocalizedTextEditorModel({
      texts: [{ text: "", language: Language.NO, translationOption: { autoTranslate: false } }],
      isReadOnly: () => false,
      label: { key: "customize-message" },
      aiClient: params.aiClient,
    });
    this.authMethod = new RadioFieldModel<MeetingAuthMethod | null>({
      value: params.documentStatus?.authMethod ?? null,
      options: [null, "PASSWORD", "LOGIN"],
      idFn: identity,
      labelFn: meetingAuthMethodLabel,
    });
    this.meetingPassword = new StringFieldModel({
      required: true,
      value: params.documentStatus?.meetingPassword ?? generatePinCode(),
    });

    makeAutoObservable(this, {}, { autoBind: true });
  }

  init(t: TFunction) {
    this.emailBody.textModels.map((d) => d.model.setInputValue(t(this.params.emailBody)));
  }

  get companyName() {
    return this.params.companyName;
  }

  get subscriptionTypeModel() {
    return this.params.subscriptionTypeModel;
  }

  get title(): TMessage {
    return this.params.document.title;
  }

  get hasInvalidCompanyRoleRecipients() {
    return this.params.hasInvalidCompanyRoleRecipients;
  }

  get canSubmit() {
    return (
      !this.isSending &&
      this.recipients.isValid &&
      !this.hasInvalidCompanyRoleRecipients &&
      (this.authMethod.value === null ||
        (this.authMethod.value === "PASSWORD" && this.meetingPassword.isValid) ||
        (this.authMethod.value === "LOGIN" && !this.params.isMissingShareholderSSN))
    );
  }

  updateShareholderEmail(item: RecipientFieldModel) {
    item.emailField.validate();
    if (typeof item.id === "number" && item.emailField.isValid && item.isChanged) {
      const onFulfilled = action((result: Result<TMessage, TMessage>, email?: string) => {
        this.updateEmailResult = result;
        if (isOk(this.updateEmailResult)) {
          item.emailField = createEmailField({ value: email, required: true });
          if (this.isSending) {
            this.sendForViewing();
          }
        } else if (isFailure(this.updateEmailResult)) {
          this.isSending = false;
        }
      });
      this.updateEmailResult = pending();
      if (item.id)
        this.params.dispatch({
          type: "dbot/update-shareholder-email",
          shareholderId: item.id,
          email: item.emailField.inputValue,
          onFulfilled: onFulfilled,
        });
    }
  }

  submit() {
    this.recipients.validate();
    if (this.canSubmit) {
      this.isSending = true;
      if (!isPending(this.updateEmailResult)) {
        this.sendForViewing();
      }
    }
  }

  sendForViewing() {
    this.params.dispatch(
      sendDocumentForViewingEvent(
        this.params.document,
        `${this.emailBody.textModels[0].model.inputValue}`,
        this.authMethod.value ? this.authMethod.value : undefined,
        this.authMethod.value === "PASSWORD" ? this.meetingPassword.validatedValue : undefined,
      ),
    );
  }

  handleEvent(event: DbotEvent) {
    switch (event.type) {
      case "dbot/send-document-for-viewing-result":
        this.params.dispatch(getViewingStatusEvent(this.params.document.documentId));
        this.params.closeDialog();
        break;
    }
  }

  get canClose() {
    return !this.isSending;
  }

  close() {
    if (this.canClose) {
      this.params.closeDialog();
    }
  }
}

type ReviewSendNoticeOfBoardMeetingDialogModelParams = {
  document: DocumentItem;
  recipients: Recipient[];
  dispatch: DbotEventDispatcher;
  closeDialog: () => void;
  subscriptionTypeModel: SubscriptionTypeModel;
  title: TMessage;
  emailBody: TMessage;
  aiClient: AIClient;
  isResend?: boolean;
  hasAllSocialSecurityNumbers: boolean;
  hasExtraParticipants: boolean;
};

export class ReviewSendNoticeOfBoardMeetingDialogModel {
  private readonly params: ReviewSendNoticeOfBoardMeetingDialogModelParams;
  recipients: RecipientsEditModel;
  emailBody: LocalizedTextEditorModel;
  isResend: boolean;
  isSending = false;
  authMethod: RadioFieldModel<MeetingAuthMethod | null>;
  meetingPassword: StringFieldModel;

  constructor(params: ReviewSendNoticeOfBoardMeetingDialogModelParams) {
    this.params = params;
    this.recipients = new RecipientsEditModel(params.recipients);
    this.emailBody = new LocalizedTextEditorModel({
      texts: [{ text: "", language: Language.NO, translationOption: { autoTranslate: false } }],
      isReadOnly: () => false,
      label: { key: "customize-message" },
      aiClient: params.aiClient,
    });
    this.isResend = params.isResend ?? false;
    this.authMethod = new RadioFieldModel<MeetingAuthMethod | null>({
      value: null,
      options: [null, "PASSWORD", "LOGIN"],
      idFn: identity,
      labelFn: meetingAuthMethodLabel,
    });
    this.meetingPassword = new StringFieldModel({ required: true, value: generatePinCode() });
    makeAutoObservable(this, {}, { autoBind: true });
  }

  init(t: TFunction) {
    this.emailBody.textModels.map((d) => d.model.setInputValue(t(this.params.emailBody)));
  }

  get title() {
    return this.params.title;
  }

  get emailBodyValue() {
    return this.emailBody.textModels[0].model.inputValue;
  }

  get canSubmit() {
    return (
      !this.isSending &&
      this.emailBodyValue !== "" &&
      (this.recipients.isValid || this.isResend) &&
      (this.authMethod.value === null ||
        (this.authMethod.value === "PASSWORD" && this.meetingPassword.isValid) ||
        (this.authMethod.value === "LOGIN" &&
          this.hasAllSocialSecurityNumbers &&
          !this.hasExtraParticipants))
    );
  }

  get hasAllSocialSecurityNumbers() {
    return this.params.hasAllSocialSecurityNumbers;
  }

  get hasExtraParticipants() {
    return this.params.hasExtraParticipants;
  }

  get subscriptionTypeModel() {
    return this.params.subscriptionTypeModel;
  }

  get showAccessTypeAndRecipients() {
    return !this.isResend;
  }

  submit() {
    if (this.isResend) {
      this.resendDocumentForViewing();
    } else {
      this.sendDocumentForViewing();
    }
  }

  resendDocumentForViewing() {
    this.isSending = true;
    this.params.dispatch(
      resendNoticeOfBoardMeetingEvent(this.params.document, `${this.emailBodyValue}`),
    );
  }

  sendDocumentForViewing() {
    this.meetingPassword.validate();
    this.recipients.validate();
    if (!this.canSubmit) {
      return;
    }

    this.isSending = true;

    if (this.recipients.isChanged) {
      this.params.dispatch(
        updateBoardMeetingRecipientsEvent(this.recipients.getChangedRecipients()),
      );
    } else {
      this.doSendDocumentForViewing();
    }
  }

  doSendDocumentForViewing() {
    this.params.dispatch(
      sendDocumentForViewingEvent(
        this.params.document,
        this.emailBodyValue,
        this.authMethod.value ?? undefined,
        this.authMethod.value === "PASSWORD" ? this.meetingPassword.validatedValue : undefined,
      ),
    );
  }

  handleEvent(event: DbotEvent) {
    switch (event.type) {
      case "dbot/send-document-for-viewing-result":
      case "dbot/resend-notice-of-board-meeting-result":
        this.params.dispatch(getViewingStatusEvent(this.params.document.documentId));
        this.params.closeDialog();
        break;
      case "dbot/update-board-meeting-recipients-result":
        this.isSending = false;
        if (isOk(event.result)) {
          this.doSendDocumentForViewing();
        } else {
          this.recipients.error = event.result.error;
        }
        break;
    }
  }

  get canClose() {
    return !this.isSending;
  }

  close() {
    if (this.canClose) {
      this.params.closeDialog();
    }
  }
}

export function meetingAuthMethodLabel(type: MeetingAuthMethod | null): TMessage {
  switch (type) {
    case null:
      return { key: "dbot.meeting.access-open" };
    default:
      return { key: "dbot.meeting.access-password" };
  }
}

type ReviewSendSubscriptionFormDialogModelParams = {
  document: DocumentItem;
  dispatch: DbotEventDispatcher;
  closeDialog: () => void;
  subscriptionTypeModel: SubscriptionTypeModel;
  aiClient: AIClient;
  user: User | null;
};

export class ReviewSendSubscriptionFormDialogModel {
  private readonly params: ReviewSendSubscriptionFormDialogModelParams;
  form: SignersForm;
  decisionId?: string;
  isSending = false;

  constructor(params: ReviewSendSubscriptionFormDialogModelParams) {
    this.params = params;
    this.form = new SignersForm({ signers: [], aiClient: params.aiClient, user: this.params.user });
    makeAutoObservable(this, {}, { autoBind: true });
  }

  get title() {
    return this.params.document.title;
  }

  get canClose() {
    return !this.isSending;
  }

  close() {
    if (this.canClose) {
      this.params.closeDialog();
    }
  }

  get canSubmit() {
    return !this.isSending && this.form.isValid;
  }

  get subscriptionTypeModel() {
    return this.params.subscriptionTypeModel;
  }

  submit() {
    this.form.validate();
    if (!this.canSubmit) {
      return;
    }
    this.isSending = true;
    this.params.dispatch(
      sendSubscriptionFormForSigningEvent(this.params.document, this.form.toSigners()),
    );
  }

  init() {
    this.params.dispatch({
      type: "dbot/fetch-increase-of-share-capital-subscription-form-signers",
    });
  }

  handleEvent(event: DbotEvent) {
    switch (event.type) {
      case "dbot/fetch-increase-of-share-capital-subscription-form-signers-result":
        this.form = new SignersForm({
          signers: event.signers,
          aiClient: this.params.aiClient,
          user: this.params.user,
        });
        this.decisionId = event.decisionId;
        break;
      case "dbot/send-subscription-form-for-signing-result":
        if (this.decisionId !== undefined) {
          this.params.dispatch(
            updateFollowUpStatusEvent(this.decisionId, {
              type: FollowUpType.subscriptionForm,
              documentId: this.params.document.documentId,
            }),
          );
        }
        this.params.closeDialog();
        break;
    }
  }
}

type ViewSubscriptionFormBatchPreviewModelParams = {
  title: TMessage | string;
  filePreview: (file: FileToPreview) => FilePreviewProps;
  context: ProjectContext;
  closeDialog: () => void;
};

export class ViewSubscriptionFormBatchPreviewModel {
  documentPreviewModel: BatchPreviewModel;
  subscribersData?: IncreaseOfShareCapitalSubscribersStatus;
  subscriptions: Map<string, SubscriptionModel> = new Map();
  selectedTab: string = "document";

  constructor(readonly params: ViewSubscriptionFormBatchPreviewModelParams) {
    makeAutoObservable(this, {}, { autoBind: true });
    this.documentPreviewModel = new BatchPreviewModel(params.title, params.filePreview);
  }

  get company() {
    return this.params.context.companyInfo.company;
  }

  get shareholders() {
    return this.params.context.shareholderData.shareholders;
  }

  setSelectedTab(value: string) {
    this.selectedTab = value;
  }

  get isDocumentTab() {
    return this.selectedTab === "document";
  }

  get isElectronicFormTab() {
    return this.selectedTab === "electronicForm";
  }

  get selectedSubscription() {
    return this.documentPreviewModel.selectedFile !== undefined
      ? (this.subscriptions.get(this.documentPreviewModel.selectedFile.reference) ?? null)
      : null;
  }

  setSubscribers(data: IncreaseOfShareCapitalSubscribersStatus) {
    this.subscribersData = data;
    if (data.type === IncreaseOfShareCapitalType.shareIssue) {
      const subscriberForIssues = filterSubscribersOnSelectedShareTypes(
        data.subscribers,
        data.selectedShareTypes,
      );
      this.documentPreviewModel.setFiles(
        subscriberForIssues.map((subscriber) =>
          toFileToPreview(subscriber.name, subscriber.subscriberid),
        ),
      );
      subscriberForIssues.forEach((subscriber) =>
        this.subscriptions.set(
          subscriber.subscriberid,
          this.toSubscriptionModel(
            subscriber,
            data.isPreferentialRights,
            data.isOverSubscriptionAllowed,
            data.subscriptionPeriod,
          ),
        ),
      );
    } else {
      this.documentPreviewModel.setFiles(
        data.subscribers.map((subscriber) =>
          toFileToPreview(subscriber.name, subscriber.subscriberid),
        ),
      );
    }
  }

  toSubscriptionModel(
    subscriber: SubscriberForIssue,
    isPreferentialRights: boolean,
    isOverSubscriptionAllowed: boolean,
    subscriptionPeriod: PeriodForSubscription,
  ) {
    return new SubscriptionModel({
      signerName: subscriber?.name ?? "",
      isShareIssue: true,
      isPreferentialRights: isPreferentialRights,
      isOversubscriptionAvailable: isOverSubscriptionAllowed,
      subscriptionPeriod: {
        from: subscriptionPeriod.from,
        to: subscriptionPeriod.to,
        hasStarted: subscriptionPeriod.from ? toDate(subscriptionPeriod.from) <= new Date() : false,
        hasEnded: subscriptionPeriod.to
          ? toDate(subscriptionPeriod.to) < new Date() && !isToday(subscriptionPeriod.to)
          : false,
      },
      subscriber: {
        name: subscriber?.name ?? "",
        organisationNumber: subscriber?.organisationNumber ?? "",
      },
      shares: subscriber?.subscriptions.map((subscription) => this.toShares(subscription)) ?? [],
      shareholder: this.shareholders.find((d) => d.id === subscriber.id),
    });
  }

  toShares(subscription: Subscription): SignSubscriptionFormShare {
    return {
      sharetypeId: subscription.sharetypeId,
      sharetypeCode: subscription.shareTypeCode,
      sharePrice: subscription.subscriptionPrice,
      availableShares: subscription.numberOfShares,
      numberOfShares: subscription.numberOfShares,
    };
  }

  close() {
    this.params.closeDialog();
  }
}

type ReviewDocumentSigningDialogModelParams = {
  orgNo: string;
  document: DocumentItem;
  signers: Signer[];
  documents: ProjectDocumentPreview[];
  companies: CompanyItem[];
  dispatch: DbotEventDispatcher;
  closeDialog: () => void;
  subscriptionTypeModel: SubscriptionTypeModel;
  infoMessage?: TMessage;
  showUnsupportedAttachmentWarning?: boolean;
  aiClient: AIClient;
  emailBody: TMessage;
  user: User | null;
  isBatchProject: boolean;
  sharepointContext?: SharepointContext;
};

export class ReviewDocumentSigningDialogModel {
  readonly params: ReviewDocumentSigningDialogModelParams;
  form: SignersForm;
  isSending = false;
  selectedDocument: ProjectDocumentPreview | undefined;
  selectedCompany: CompanyItem | undefined;

  constructor(params: ReviewDocumentSigningDialogModelParams) {
    this.params = params;
    this.setSelectedCompany(this.companies[0]);
    this.selectedDocument = this.documents.length > 0 ? this.documents[0] : undefined;
    this.form = new SignersForm({
      ...params,
      sendSealedCopyToDefaultSelection: params.isBatchProject ? [] : undefined,
      showBatchEmailsSettings: params.isBatchProject,
      sharepointContext: params.sharepointContext,
    });
    this.form.validate();
    makeAutoObservable(this, {}, { autoBind: true });
  }

  get companies() {
    return this.params.companies.filter((company) =>
      this.params.documents.some((document) => document.orgNo === company.organisationNumber),
    );
  }

  get companiesForSigning(): CompaniesForSigning | undefined {
    return this.selectedCompany !== undefined
      ? {
          companies: this.companies,
          selectedCompany: this.selectedCompany,
          selectCompany: this.setSelectedCompany,
          previousCompany: when(
            prev(
              this.companies,
              (s) => s.organisationNumber === this.selectedCompany?.organisationNumber,
            ),
            (s) => () => this.setSelectedCompany(s),
          ),
          nextCompany: when(
            next(
              this.companies,
              (s) => s.organisationNumber === this.selectedCompany?.organisationNumber,
            ),
            (s) => () => this.setSelectedCompany(s),
          ),
        }
      : undefined;
  }

  setSelectedCompany(company?: CompanyItem) {
    this.selectedCompany = company;
    if (this.documents.length > 0) {
      this.setSelectedDocument(this.documents[0]);
    }
  }

  get documents() {
    return this.selectedCompany
      ? this.params.documents.filter(
          (document) => document.orgNo === this.selectedCompany?.organisationNumber,
        )
      : this.params.documents;
  }

  get documentsForSigning(): DocumentsForSigning | undefined {
    return this.selectedDocument !== undefined
      ? {
          documents: this.documents.map((a) => ({
            id: a.documentId,
            name: a.documentTitle,
            reference: a.reference,
          })),
          selectedDocument: {
            id: this.selectedDocument.documentId,
            name: this.selectedDocument.documentTitle,
            reference: this.selectedDocument.reference,
          },
          selectDocument: (document) =>
            this.setSelectedDocument(this.documentById(document.id, document.reference)),
          previousDocument: when(
            prev(
              this.documents,
              (s) =>
                s.documentId === this.selectedDocument?.documentId &&
                s.reference === this.selectedDocument?.reference,
            ),
            (s) => () => this.setSelectedDocument(s),
          ),
          nextDocument: when(
            next(
              this.documents,
              (s) =>
                s.documentId === this.selectedDocument?.documentId &&
                s.reference === this.selectedDocument?.reference,
            ),
            (s) => () => this.setSelectedDocument(s),
          ),
        }
      : undefined;
  }

  setSelectedDocument(document?: ProjectDocumentPreview) {
    this.selectedDocument = document;
  }

  documentById(id: string, reference?: string) {
    return this.documents.find((a) => a.documentId === id && a.reference === reference);
  }

  get showUnsupportedAttachmentWarning() {
    return this.params.showUnsupportedAttachmentWarning;
  }

  get title(): TMessage {
    return this.params.document.title;
  }

  get canClose() {
    return !this.isSending;
  }

  close() {
    if (this.canClose) {
      this.params.closeDialog();
    }
  }

  get canSubmit() {
    return !this.isSending && this.form.isValid;
  }

  get subscriptionTypeModel() {
    return this.params.subscriptionTypeModel;
  }

  get infoMessage() {
    return this.params.infoMessage;
  }

  documentPreviewUrl(documentId: string) {
    return this.showUnsupportedAttachmentWarning
      ? `/api/document/view/pdf/${documentId}`
      : `/api/document/view/pdf/${documentId}?includeAttachments=true`;
  }

  documentRecipientPreviewUrl(documentId: string, recipient: string) {
    return this.showUnsupportedAttachmentWarning
      ? `/api/document/view/pdf/${documentId}?recipientId=${recipient}`
      : `/api/document/view/pdf/${documentId}?recipientId=${recipient}&includeAttachments=true`;
  }

  documentPreview({ t }: { t: TFunction }): FilePreviewProps | undefined {
    if (this.selectedDocument !== undefined) {
      const reference = resolveUrnReference(this.selectedDocument?.reference);
      return {
        filename: `${this.selectedDocument.documentTitle}.pdf`,
        file: {
          url:
            reference !== undefined && reference.type === "recipientId"
              ? this.documentRecipientPreviewUrl(this.selectedDocument.documentId, reference.id)
              : this.documentPreviewUrl(this.selectedDocument.documentId),
        },
      };
    } else {
      return {
        filename: `${t(this.params.document.title)}.pdf`,
        file: {
          url: this.documentPreviewUrl(this.params.document.documentId),
        },
      };
    }
  }

  submit() {
    this.form.validate();

    if (!this.canSubmit) {
      return;
    }

    this.isSending = true;

    if (this.form.hasChangedEmails) {
      this.params.dispatch(
        updateCompanyRoleEmailsEvent(
          this.params.orgNo,
          compact(this.form.getSignersWithChangedEmail().map(mapSignerToUpdateCompanyRoleItem)),
        ),
      );
    } else {
      this.params.dispatch(
        sendDocumentForSigningEvent(
          this.params.document,
          this.form.toSigners(),
          this.form.emailBody.textModels[0].model.inputValue,
          this.form.toSigningSettings(),
        ),
      );
    }
  }

  handleEvent(event: DbotEvent) {
    switch (event.type) {
      case "dbot/update-company-role-emails-result":
        if (isOk(event.result)) {
          this.params.dispatch(
            sendDocumentForSigningEvent(
              this.params.document,
              this.form.toSigners(),
              this.form.emailBody.textModels[0].model.inputValue,
              this.form.toSigningSettings(),
            ),
          );
        } else {
          this.isSending = false;
          this.form.error = event.result.error;
        }
        break;
      case "dbot/send-document-for-signing-result":
        if (isOk(event.result)) {
          if (event.userPrefs && isAlertDismissed(event.userPrefs, "signingImprovements")) {
            this.params.dispatch({
              type: "show-alert",
              message: { key: "dbot.sendDocumentForSigningSuccess" },
              messageType: "success",
            });
          } else {
            this.params.dispatch(showSubmitForSigningSuccess(this.params.orgNo));
          }
        }
        this.params.closeDialog();
        break;
    }
  }
}

export type SendForReviewDialogModelParams = {
  baseUrl: string;
  orgNo: string;
  projectId: string;
  template: SendForReviewMessageTemplate;
  document: DocumentItem;
  dispatch: DbotEventDispatcher;
  closeDialog: () => void;
  roles: CompanyRole[];
  canBeSentForVoting: boolean;
  recipients?: CompanyRoleIdWithName[];
  context: ProjectContext;
};

export type SendForReviewDialogView = "options" | "recipients";

export type OptionsBeforeSending = {
  mode: "commenting" | "voting";
  deadlineForFeedback?: string;
};

export class RecipientModel {
  name: StringFieldModel;
  email: StringFieldModel;

  constructor() {
    makeAutoObservable(this);
    this.name = new StringFieldModel({ required: true });
    this.email = new StringFieldModel({
      required: true,
      validator: emailValidator,
    });
  }

  get isValid(): boolean {
    return this.name.isValid && this.email.isValid;
  }

  validate() {
    this.name.validate();
    this.email.validate();
    return this.isValid;
  }
}

export class SendForReviewDialogModel {
  readonly params: SendForReviewDialogModelParams;
  error?: TMessage;
  isSending?: boolean;
  view: SendForReviewDialogView = "options";
  subject = "";
  body = "";
  link = "";
  excludedRecipients = new Set<CompanyroleId>();
  deadlineForFeedback = false;
  enabledVotingOnDecisionMatters = false;
  deadlineDate: LocalDateFieldModel;
  deadlineTime: TimeFieldModel;
  disableVoting = false;
  synchronizeDeadlineDateInBody = true;

  extraRecipients: RecipientModel[] = [];

  constructor(params: SendForReviewDialogModelParams) {
    this.params = params;
    makeAutoObservable(this, {}, { autoBind: true });
    this.deadlineDate = new LocalDateFieldModel({ required: this.deadlineForFeedback });
    this.deadlineTime = new TimeFieldModel({
      value: "",
    });
    this.recipients
      .filter(
        (role) =>
          params.recipients?.findIndex((r) => r.companyroleId === role.companyroleId) === -1,
      )
      .forEach((r) => this.toggleRecipient(r.companyroleId));
  }

  init(t: TFunction) {
    this.subject = t(this.params.template.subject);
    this.body = t(this.params.template.body);
    this.link = `[${t(this.params.template.linkText)}](${joinUrl(
      this.params.baseUrl,
      boardMemberBoardMeetings(this.params.orgNo, this.params.projectId, "minutes"),
    )})`;
  }

  toggleDeadlineForFeedback() {
    this.deadlineForFeedback = !this.deadlineForFeedback;
    this.deadlineDate.setRequired(this.deadlineForFeedback);
  }

  toggleEnableVotingOnDecisionMatter() {
    this.enabledVotingOnDecisionMatters = !this.enabledVotingOnDecisionMatters;
  }

  updateDeadlineFeedbackDate(t: TFunction) {
    if (!this.deadlineDate.isEmpty && this.synchronizeDeadlineDateInBody) {
      this.body = this.toText(t);
    }
  }

  private toText(t: TFunction) {
    return t("dbot.send-for-review.board-minutes.message-body", {
      deadline: formatDate(this.params.context.locale, this.deadlineDate.inputValue),
      senderName: this.params.template.senderName,
    });
  }

  get recipients(): CompanyRole[] {
    return sortedRoles(this.params.roles);
  }

  isCompanyRole(row: RecipientModel | CompanyRole): row is CompanyRole {
    return "companyroleId" in row;
  }

  getId(row: RecipientModel | CompanyRole): string | number {
    if (this.isCompanyRole(row)) {
      return row.companyroleId;
    } else {
      return this.extraRecipients.indexOf(row);
    }
  }

  get recipientCount() {
    return this.recipients.length + this.extraRecipients.length - this.excludedRecipients.size;
  }

  get viewOptions(): SendForReviewDialogView[] {
    return ["options", "recipients"];
  }

  get votingOnDecisionMatterLabel(): TMessage {
    if (this.params.context.currentDecisions.length === 1) {
      return {
        key: "commenting.matter.name",
        params: { name: first(this.params.context.currentDecisions)?.title },
      };
    }
    return {
      key: "commenting.count",
      params: { count: this.params.context.currentDecisions.length },
    };
  }

  get disableVotingOnMatter(): boolean {
    return this.params.context.currentDecisions.length < 1 && !this.disableVoting;
  }

  get canSubmit(): boolean {
    return this.recipientCount === 0 && this.extraRecipients.length === 0;
  }

  get isAllSelected(): boolean {
    return this.recipients.every((r) => !this.excludedRecipients.has(r.companyroleId));
  }

  toggleAllRecipients(): void {
    if (this.isAllSelected) {
      this.recipients.forEach((recipient) => this.excludedRecipients.add(recipient.companyroleId));
    } else {
      this.excludedRecipients.clear();
    }
  }

  setView(v: SendForReviewDialogView) {
    this.view = v;
  }

  infoButtonMessage(): TMessage {
    if (!this.disableVotingOnMatter) {
      return { key: "commenting.info" };
    }
    return { key: "commenting.info.not.found" };
  }

  viewLabel(v: SendForReviewDialogView): TMessage {
    switch (v) {
      case "options":
        return { key: "options" };
      case "recipients":
        return {
          key: "dbot.send-for-review.board-minutes.recipients",
          params: { n: this.recipientCount },
        };
    }
  }

  setBody(value: string, t: TFunction) {
    const text = this.toText(t);
    this.synchronizeDeadlineDateInBody = isBlank(this.deadlineDate.inputValue) || text === value;
    this.body = value;
  }

  toggleRecipient(id: CompanyroleId) {
    if (this.excludedRecipients.has(id)) {
      this.excludedRecipients.delete(id);
    } else {
      this.excludedRecipients.add(id);
    }
  }

  handleEvent(event: DbotEvent) {
    switch (event.type) {
      case "dbot/send-for-review-result":
        if (isOk(event.result)) {
          this.params.closeDialog();
          this.params.dispatch({
            type: "show-alert",
            messageType: "success",
            message: { key: "dbot.send-for-review.success" },
          });
        } else {
          this.isSending = false;
          this.error = { key: "dbot.send-for-review.error" };
        }
        break;
    }
  }

  prepareData(language: Language): SendForReviewEvent {
    return {
      type: "dbot/send-for-review",
      documentId: this.params.document.documentId,
      sendMessageRequest: {
        language: language,
        messageSubject: this.subject,
        payload: `${this.body}`,
        isForBoardMembers: true,
        recipientSelectionState: {
          excludeBoardMemberIds: Array.from(this.excludedRecipients),
          inlineRecipients: this.extraRecipients.map((r) => ({
            name: r.name.validatedValue,
            email: r.email.validatedValue,
          })),
        },
      },
      options: {
        mode: this.enabledVotingOnDecisionMatters ? "voting" : "commenting",
        deadlineForFeedback: this.deadlineForFeedback
          ? isNotBlank(this.deadlineTime.validatedValue)
            ? combineDateAndTime(
                this.deadlineDate.validatedValue!,
                this.deadlineTime.validatedValue!,
              )
            : combineDateAndTime(this.deadlineDate.validatedValue!, "00:00")
          : undefined,
      },
    };
  }

  addRecipient() {
    this.extraRecipients.push(new RecipientModel());
  }

  canDeleteRecipient(row: RecipientModel | CompanyRole) {
    return !this.isCompanyRole(row);
  }

  deleteRecipient(row: RecipientModel | CompanyRole) {
    if (!this.isCompanyRole(row)) {
      this.extraRecipients.splice(this.extraRecipients.indexOf(row), 1);
    }
  }

  validate(): boolean {
    return [this.deadlineDate, ...this.extraRecipients].reduce(
      (acc, m) => m.validate() && acc,
      true,
    );
  }

  submit(language: Language) {
    if (this.validate()) {
      this.isSending = true;
      this.error = undefined;
      this.params.dispatch(this.prepareData(language));
    } else {
      this.error = { key: "general-validation-error" };
    }
  }

  cancel() {
    if (!this.isSending) {
      this.params.closeDialog();
    }
  }

  getRoleLabel(r: CompanyRole) {
    switch (r.role) {
      case RoleType.CHAIRPERSON:
      case RoleType.CEO:
      case RoleType.DEPUTY_CHAIRPERSON:
      case RoleType.DEPUTY_MEMBER:
      case RoleType.OBSERVER:
        return getRoleTypeLabel(r.role);
    }
  }
}

export type ResendDocumentForSigningData = {
  name: string;
  email: string;
  language: Language;
  signingMethod: SigningMethod;
};

export type ViewDocumentSigningStatusDialogModelParams = {
  documentId?: string;
  documentType: DocumentType | ArchiveDocumentType;
  title: TMessage;
  getSigningStatus(): void;
  downloadSignedDocument?(): void;
  resendDocumentForSigning?(signedDocumentId: string, data: ResendDocumentForSigningData): void;
  closeDialog: () => void;
  confirmDeleteSignatureRequest?: () => void;
};

export class ViewDocumentSigningStatusDialogModel {
  readonly params: ViewDocumentSigningStatusDialogModelParams;
  documentSigningStatus: Result<GetDocumentSigningStatusResponse, TMessage> = pending();
  alertMessage?: {
    type: "success" | "error";
    message: TMessage;
  };
  resentSignedDocumentIds: string[] = [];

  state:
    | { type: "ready" }
    | { type: "reloading" }
    | { type: "delete-signature-request"; isLoading: boolean }
    | { type: "resending"; signedDocumentId: string }
    | { type: "edit"; signedDocumentId: string; model: SignerModel; isSending?: boolean } = {
    type: "ready",
  };

  constructor(params: ViewDocumentSigningStatusDialogModelParams) {
    this.params = params;
    makeAutoObservable(this, {}, { autoBind: true });
  }

  init() {
    this.params.getSigningStatus();
  }

  get confirmCancelSignatureRequest() {
    return this.params.confirmDeleteSignatureRequest;
  }

  get hideCancelSignatureRequestButton() {
    return (
      isOk(this.documentSigningStatus) &&
      this.documentSigningStatus.data.documentSigningStatus !==
        DocumentSigningStatus.SIGNING_COMPLETE
    );
  }

  setSigningStatusResult(result: Ok<GetDocumentSigningStatusResponse> | Failure<TMessage>) {
    this.setAlertMessage(result);
    this.documentSigningStatus = result;
    this.state = { type: "ready" };
  }

  setAlertMessage(result: Ok<GetDocumentSigningStatusResponse> | Failure<TMessage>) {
    if (this.state.type === "resending") {
      if (isOk(result)) {
        const signer = this.signerBySignedDocumentId(this.state.signedDocumentId);
        this.resentSignedDocumentIds.push(this.state.signedDocumentId);
        this.alertMessage = {
          type: "success",
          message: {
            key: "dbot.resendDocumentForSigingSuccess",
            params: {
              documentType: this.title,
              name: signer?.name,
              email: signer?.email,
            },
          },
        };
      } else if (isFailure(result)) {
        this.alertMessage = { type: "error", message: result.error };
      }
    }
  }

  signerBySignedDocumentId(signedDocumentId: string): SigningStatusForSigner | undefined {
    return isOk(this.documentSigningStatus)
      ? this.documentSigningStatus.data.signerStatus.find(
          (d) => d.signedDocumentId === signedDocumentId,
        )
      : undefined;
  }

  get groupedSigners(): Array<[orgNo: string, SigningStatusForSigner[]]> {
    return isOk(this.documentSigningStatus)
      ? Object.entries(
          groupBy(this.documentSigningStatus.data.signerStatus, (s) => s.companyName ?? ""),
        ).map(([orgNo, signers]) => [orgNo, sortBy(signers, "name")])
      : [];
  }

  showGroupHeading(companyName: string): boolean {
    return Boolean(companyName) && this.groupedSigners.length > 1;
  }

  get canDownload(): boolean {
    return (
      this.params.downloadSignedDocument !== undefined &&
      isOk(this.documentSigningStatus) &&
      this.documentSigningStatus.data.signerStatus.every((s) => s.isSigned)
    );
  }

  download() {
    this.params.downloadSignedDocument?.();
    this.params.closeDialog();
  }

  reload() {
    this.params.getSigningStatus();
    this.state = { type: "reloading" };
  }

  get title() {
    return this.params.title;
  }

  get showSubmitButton(): boolean {
    return (
      (isOk(this.documentSigningStatus) &&
        this.params.documentType !== DocumentType.subscription_form) ||
      (isOk(this.documentSigningStatus) &&
        this.params.documentType === DocumentType.subscription_form &&
        !this.canDownload)
    );
  }

  get isSubmitDisabled() {
    return this.state.type !== "ready";
  }

  get isSubmitting(): boolean {
    return this.state.type === "reloading";
  }

  get submit() {
    if (this.canDownload) {
      return this.download;
    } else {
      return this.reload;
    }
  }

  get submitLabel(): TMessage | undefined {
    if (this.canDownload) {
      return { key: "see_signed" };
    } else {
      return { key: "signing.refresh" };
    }
  }

  get showCloseButton(): boolean {
    return !isPending(this.documentSigningStatus);
  }

  get isCloseDisabled() {
    return this.state.type !== "ready";
  }

  close() {
    this.params.closeDialog();
  }

  onClose() {
    this.state.type = "ready";
  }

  isResending(item: SigningStatusForSigner) {
    return this.state.type === "resending" && this.state.signedDocumentId === item.signedDocumentId;
  }

  isResentToSigner(item: SigningStatusForSigner) {
    return this.resentSignedDocumentIds.includes(item.signedDocumentId);
  }

  isResendDisabled(item: SigningStatusForSigner) {
    return this.state.type !== "ready" || this.isResentToSigner(item);
  }

  showResendButton(item: SigningStatusForSigner): boolean {
    return !item.isSigned && this.params.resendDocumentForSigning !== undefined;
  }

  openConfirmDeleteSigningRequestDialog() {
    this.state.type = "delete-signature-request";
  }

  setReopenResult(result: Result) {
    if (isOk(result)) {
      this.onClose();
    } else if (isFailure(result)) {
      this.alertMessage = { type: "error", message: { key: "general-error" } };
    }
  }

  get showCopyLinkButton(): boolean {
    return (
      !isOk(this.documentSigningStatus) ||
      this.documentSigningStatus?.data.documentSigningStatus !==
        DocumentSigningStatus.SIGNING_COMPLETE
    );
  }

  resend(item: SigningStatusForSigner) {
    if (this.state.type !== "ready") {
      return;
    }
    this.state = { type: "resending", signedDocumentId: item.signedDocumentId };
    this.params.resendDocumentForSigning?.(item.signedDocumentId, {
      name: item.name,
      email: item.email,
      language: item.language ?? DEFAULT_LANG,
      signingMethod: item.signingMethod,
    });
  }

  copySigningLinkToClipboard(signer: SigningStatusForSigner) {
    if (signer.signingUrl) {
      navigator.clipboard
        .writeText(signer.signingUrl)
        .then(() => {
          this.alertMessage = {
            type: "success",
            message: { key: "signing.link.success" },
          };
        })
        .catch((err) => {
          logError(err, "Unable to copy the recipient's document signing link");
          this.alertMessage = {
            type: "error",
            message: { key: "general-error" },
          };
        });
    }
  }

  tooltipLabel(signer: SigningStatusForSigner): TMessage {
    return signer.signingMethod === SigningMethod.HANDWRITING
      ? { key: "signing.link.tooltip.handwriting" }
      : { key: "signing.link.tooltip", params: { name: signer.name } };
  }

  isEditDisabled() {
    return this.state.type !== "ready";
  }

  showEditButton(item: SigningStatusForSigner) {
    return !item.isSigned && this.params.resendDocumentForSigning !== undefined;
  }

  edit(item: SigningStatusForSigner) {
    this.state = {
      type: "edit",
      signedDocumentId: item.signedDocumentId,
      model: new SignerModel({
        ...item,
        id: item.signedDocumentId,
        language: item.language ?? DEFAULT_LANG,
      }),
    };
  }

  showCancelEditButton() {
    return !this.isSendingEditedSigner();
  }

  cancelEdit() {
    this.state = { type: "ready" };
  }

  sendEditedSigner() {
    if (
      this.state.type !== "edit" ||
      this.state.isSending ||
      !this.state.model.validate() ||
      this.params.resendDocumentForSigning === undefined
    ) {
      return;
    }
    this.state.isSending = true;
    this.params.resendDocumentForSigning(this.state.signedDocumentId, {
      name: this.state.model.nameField.validatedValue,
      email: this.state.model.emailField.validatedValue!,
      language: this.state.model.languageField.value,
      signingMethod: this.state.model.methodField.value,
    });
  }

  isSendingEditedSigner() {
    return this.state.type === "edit" && this.state.isSending;
  }

  clearAlertMessage() {
    this.alertMessage = undefined;
  }
}

export type ViewSubscriptionStatusDialogModelParams = {
  documentId: string;
  title: TMessage;
  getSubscriptionStatus(): void;
  closeDialog: () => void;
};

export class ViewSubscriptionStatusDialogModel {
  readonly params: ViewSubscriptionStatusDialogModelParams;
  subscriptionStatus: Result<GetSubscriptionStatusResponse, TMessage> = pending();

  state:
    | { type: "ready" }
    | { type: "reloading" }
    | { type: "resending"; signedDocumentId: string }
    | { type: "edit"; signedDocumentId: string; model: SignerModel; isSending?: boolean } = {
    type: "ready",
  };

  constructor(params: ViewSubscriptionStatusDialogModelParams) {
    this.params = params;
    makeAutoObservable(this, {}, { autoBind: true });
  }

  init() {
    this.params.getSubscriptionStatus();
  }

  setSubscriptionStatusResult(result: Ok<GetSubscriptionStatusResponse> | Failure<TMessage>) {
    this.subscriptionStatus = result;
    this.state = { type: "ready" };
  }

  get subscriptions(): SubscriptionStatus[] {
    return isOk(this.subscriptionStatus)
      ? sortBy(this.subscriptionStatus.data.subscriptions, "name")
      : [];
  }

  reload() {
    this.params.getSubscriptionStatus();
    this.state = { type: "reloading" };
  }

  get showSubmitButton(): boolean {
    return isOk(this.subscriptionStatus);
  }

  get isSubmitDisabled() {
    return this.state.type !== "ready";
  }

  get isSubmitting(): boolean {
    return this.state.type === "reloading";
  }

  get submit() {
    return this.reload;
  }

  get submitLabel(): TMessage | undefined {
    return { key: "signing.refresh" };
  }

  get showCloseButton(): boolean {
    return !isPending(this.subscriptionStatus);
  }

  get isCloseDisabled() {
    return this.state.type !== "ready";
  }

  close() {
    this.params.closeDialog();
  }

  get title() {
    return this.params.title;
  }
}

type ViewDocumentViewingStatusDialogModelParams = {
  document: DocumentItem;
  dispatch: DbotEventDispatcher;
  closeDialog: () => void;
};

export class RecipientViewModel {
  emailField: StringFieldModel;
  companyroleId?: CompanyroleId;
  boardMeetingId?: string;
  shareholderId?: number;
  recipient: DocumentViewingStatus;

  constructor(recipient: DocumentViewingStatus) {
    this.recipient = recipient;
    this.emailField = createEmailField({ value: recipient.recipientEmail, required: true });
    this.companyroleId = recipient.companyroleId;
    this.boardMeetingId = recipient.boardMeetingId;
    this.shareholderId = recipient.shareholderId;
    makeAutoObservable(this, {}, { autoBind: true });
  }

  get id(): string | number | undefined {
    return this.recipient.recipientDocumentId;
  }

  get isValid(): boolean {
    return this.emailField.isValid;
  }

  get name(): string {
    return this.recipient.recipientName;
  }

  validate() {
    this.emailField.validate();
    return this.isValid;
  }

  prepareUpdatedRecipientData(): ResendDocumentForViewing {
    return {
      recipientName: this.name,
      recipientEmail: this.emailField.validatedValue!,
      companyroleId: this.companyroleId,
      boardMeetingId: this.boardMeetingId,
      shareholderId: this.shareholderId,
      recipientDocumentId: this.recipient.recipientDocumentId,
    };
  }
}

export class ViewDocumentViewingStatusDialogModel {
  params: ViewDocumentViewingStatusDialogModelParams;
  status: Result<GetDocumentViewingStatusResponse, TMessage> = pending();
  isReloading = false;
  alertMessage?: {
    type: "success" | "error";
    message: TMessage;
  };

  constructor(params: ViewDocumentViewingStatusDialogModelParams) {
    this.params = params;
    makeAutoObservable(this, {}, { autoBind: true });
  }

  state:
    | { type: "ready" }
    | { type: "reloading" }
    | { type: "resending"; viewDocumentId: string }
    | { type: "edit"; viewDocumentId: string; model: RecipientViewModel; isSending?: boolean } = {
    type: "ready",
  };

  get recipientView(): DocumentViewingStatus[] {
    return isOk(this.status) ? sortBy(this.status.data.statusForViewers, "recipientName") : [];
  }

  init() {
    this.params.dispatch(getViewingStatusEvent(this.params.document.documentId));
  }

  setState(state: ViewDocumentViewingStatusDialogModel["state"]) {
    this.alertMessage = undefined;
    this.state = state;
  }

  cancelEdit() {
    this.setState({ type: "ready" });
  }

  isSendingEditedRecipient() {
    return this.state.type === "edit" && this.state.isSending;
  }

  sendUpdatedRecipient() {
    if (this.state.type !== "edit" || this.state.isSending || !this.state.model.validate()) {
      return;
    }
    this.state.isSending = true;
    this.params.dispatch({
      type: "dbot/resend-notice-to-updated-recipient",
      documentId: this.params.document.documentId,
      data: this.state.model.prepareUpdatedRecipientData(),
    });
  }

  showCancelEditButton() {
    return !this.isSendingEditedRecipient();
  }

  isEditDisabled() {
    return this.state.type !== "ready";
  }

  edit(recipient: DocumentViewingStatus) {
    this.setState({
      type: "edit",
      viewDocumentId: recipient.recipientDocumentId,
      model: new RecipientViewModel({
        ...recipient,
        shareholderId: recipient.shareholderId,
        companyroleId: recipient.companyroleId,
        boardMeetingId: recipient.boardMeetingId,
      }),
    });
  }

  isResending(resend: DocumentViewingStatus) {
    return (
      this.state.type === "resending" && this.state.viewDocumentId === resend.recipientDocumentId
    );
  }

  isResendDisabled() {
    return this.state.type !== "ready";
  }

  resend(data: DocumentViewingStatus) {
    if (this.state.type !== "ready") {
      return;
    }
    this.setState({ type: "resending", viewDocumentId: this.params.document.documentId });
    this.params.dispatch({
      type: "dbot/resend-notice-to-updated-recipient",
      documentId: this.params.document.documentId,
      data,
    });
  }

  handleEvent(event: DbotEvent) {
    switch (event.type) {
      case "dbot/get-viewing-status-result":
        this.status = event.result;
        this.isReloading = false;
        switch (this.state.type) {
          case "resending":
            this.alertMessage = {
              type: "success",
              message: { key: "dbot.resendDocumentForUpdatingSuccess" },
            };
            this.state = { type: "ready" };
            break;
          case "edit":
            if (this.state.isSending) {
              this.alertMessage = {
                type: "success",
                message: { key: "dbot.resendDocumentForUpdatingSuccess" },
              };
              this.state = { type: "ready" };
            }
        }
        break;
      case "dbot/resend-notice-to-updated-recipient-result":
        if (isFailure(event.result)) {
          this.state = { type: "ready" };
          this.alertMessage = { type: "error", message: event.result.error };
        } else {
          this.params.dispatch(getViewingStatusEvent(this.params.document.documentId));
        }
        break;
    }
  }

  get showReloadButton() {
    return !isPending(this.status);
  }

  reload() {
    this.params.dispatch(getViewingStatusEvent(this.params.document.documentId));
    this.isReloading = true;
  }

  get title() {
    return this.params.document.title;
  }

  close() {
    this.params.closeDialog();
  }

  clearAlertMessage() {
    this.alertMessage = undefined;
  }
}

export type ReopenLockedDocumentModelParams = {
  onSubmit: () => void;
  onCancel: () => void;
};

export class ReopenLockedDocumentDialogModel {
  onSubmit: () => void;
  onCancel: () => void;
  reopenResult?: Result;

  constructor(params: ReopenLockedDocumentModelParams) {
    this.onSubmit = () => {
      this.reopenResult = pending();
      params.onSubmit();
    };
    this.onCancel = params.onCancel;
    makeAutoObservable(this, {}, { autoBind: true });
  }

  isLoading() {
    return isPending(this.reopenResult);
  }

  setReopenResult(result: Result) {
    this.reopenResult = result;
  }
}

export class RecipientsEditModel {
  recipientFields: RecipientFieldModel[];
  error?: TMessage;

  constructor(
    recipients: Recipient[],
    readonly onChange?: (type: "change" | "add" | "remove", m: RecipientFieldModel) => void,
  ) {
    makeAutoObservable(this, {}, { autoBind: true });
    this.recipientFields = recipients.map(
      (r) => new RecipientFieldModel(r, (m) => onChange?.("change", m)),
    );
    this.validate();
  }

  validate(): boolean {
    this.recipientFields.forEach((r) => r.validate());
    return this.isValid;
  }

  get count() {
    return this.recipientFields.length;
  }

  get isEmpty() {
    return this.recipientFields.length <= 0;
  }

  get isValid(): boolean {
    return !this.isEmpty && this.recipientFields.every((r) => r.isValid);
  }

  get isChanged(): boolean {
    return this.recipientFields.some((r) => r.isChanged);
  }

  getChangedRecipients(): Recipient[] {
    return this.recipientFields.filter((r) => r.isChanged).map((r) => r.toRecipient());
  }

  addRecipient(r: Recipient) {
    if (!this.recipientFields.some((x) => x.id === r.id || (r.ref && r.ref === x.ref))) {
      const m = new RecipientFieldModel(r, (m) => this.onChange?.("change", m));
      this.recipientFields.push(m);
      this.onChange?.("add", m);
    }
  }

  removeRecipient(id: RecipientId) {
    const m = this.recipientFields.find((r) => r.id === id);
    if (m) {
      this.recipientFields.splice(this.recipientFields.indexOf(m), 1);
      this.onChange?.("remove", m);
    }
  }

  toRecipients(): Recipient[] {
    return this.recipientFields.map((r) => r.toRecipient());
  }
}

export class RecipientFieldModel {
  nameField: StringFieldModel;
  emailField: StringFieldModel;

  constructor(
    readonly recipient: Recipient,
    readonly onChange?: (r: RecipientFieldModel) => void,
  ) {
    makeAutoObservable(this, {}, { autoBind: true });
    this.nameField = new StringFieldModel({
      value: recipient.name,
      onChange: () => onChange?.(this),
    });
    this.emailField = createEmailField({
      value: recipient.email,
      required: true,
      onChange: () => onChange?.(this),
    });
  }

  get id(): RecipientId {
    return this.recipient.id;
  }

  get ref(): string | undefined {
    return this.recipient.ref;
  }

  get isValid() {
    return this.nameField.isValid && this.emailField.isValid;
  }

  get isChanged() {
    return this.nameField.isChanged || this.emailField.isChanged;
  }

  validate(): boolean {
    this.nameField.validate();
    this.emailField.validate();
    return this.isValid;
  }

  toRecipient(): Recipient {
    return {
      id: this.recipient.id,
      name: this.nameField.validatedValue,
      email: this.emailField.validatedValue ?? "",
      att: this.recipient.att,
      ref: this.recipient.ref,
    };
  }

  rebase() {
    this.nameField.rebase();
    this.emailField.rebase();
  }
}

///
/// Actions
///

export type DecisionAction =
  | ApplyDecisionAction
  | ConfirmSendToBRAction
  | UploadAttachmentsEvent
  | UploadAttachmentsResultEvent
  | DeleteAttachmentEvent
  | DeleteAttachmentResultEvent
  | DownloadAttachmentEvent
  | OpenIncreaseOfShareCapitalSubscribersStatusEvent
  | OpenIncreaseOfShareCapitalSubscribersAllocationEvent;

export type DocumentAction =
  | DownloadDocumentAction
  | DownloadSubscriptionFormsAction
  | OpenSubscriptionFormBatchPreviewDialogAction
  | OpenReviewDialogAction
  | OpenSigningStatusDialogAction
  | OpenViewingStatusDialogAction
  | OpenSendForReviewDialogAction
  | RefreshSigningStatusesAction
  | ValidateProjectAction
  | AutosignDocumentEvent
  | UnlockDocumentAction
  | OpenSubscriptionStatusEvent;

export const defaultReviewDeadlineDays = 7;

export function sendBoardMinutesForReviewTemplate({
  locale,
  user,
}: {
  user: User;
  locale: Language;
}): SendForReviewMessageTemplate {
  return {
    subject: { key: "dbot.send-for-review.board-minutes.message-subject" },
    body: {
      key: "dbot.send-for-review.board-minutes.message-body",
      params: {
        deadline: formatDate(locale, plusDays(Date.now(), defaultReviewDeadlineDays)),
        senderName: getFullName(user),
      },
    },
    linkText: { key: "dbot.send-for-review.board-minutes.link-text" },
    senderName: getFullName(user),
  };
}

export function openSendForReviewDialogAction(
  {
    document,
    signingStatus = DocumentSigningStatus.UNKNOWN,
    recipients,
  }: {
    document: DocumentItem;
    signingStatus?: DocumentSigningStatus;
    recipients?: CompanyRoleIdWithName[];
  },
  context: { user: User; locale: Language },
): OpenSendForReviewDialogAction | undefined {
  if (
    signingStatus === DocumentSigningStatus.UNKNOWN ||
    signingStatus === DocumentSigningStatus.SENT_FOR_REVIEW ||
    signingStatus === DocumentSigningStatus.SENT_FOR_VOTING
  ) {
    switch (document.type) {
      case DocumentType.board_meeting:
        return {
          type: "dbot/open-send-for-review-dialog",
          document,
          recipients,
          label: { key: "dbot.send-for-review" },
          messageTemplate: sendBoardMinutesForReviewTemplate(context),
        };
    }
  }
}

export function downloadSignedDocumentAction({
  document,
  signingStatus = DocumentSigningStatus.UNKNOWN,
  numberOfSignedDocuments,
}: {
  document: DocumentItem;
  signingStatus?: DocumentSigningStatus;
  numberOfSignedDocuments?: number;
}): DocumentAction | null {
  if (numberOfSignedDocuments !== undefined && numberOfSignedDocuments > 1) {
    return {
      document,
      type: "dbot/download-document",
      label: {
        key: "see_signed_documents",
        params: { n: numberOfSignedDocuments ?? 0 },
      },
    };
  } else if (
    numberOfSignedDocuments === 1 ||
    signingStatus === DocumentSigningStatus.SIGNING_COMPLETE
  ) {
    return { document, type: "dbot/download-document", label: { key: "see_signed" } };
  }
  return null;
}

export function downloadSignedSubscriptionFormsAction({
  document,
  numberOfSignedDocuments,
  isSentForSigning = false,
}: {
  document: DocumentItem;
  numberOfSignedDocuments?: number;
  isSentForSigning?: boolean;
}): DocumentAction | null {
  if (isSentForSigning) {
    return {
      document,
      type: "dbot/download-subscription-forms",
      label: {
        key: "see_signed_documents",
        params: { n: numberOfSignedDocuments ?? 0 },
      },
    };
  }
  return null;
}

export function openSubscriptionFormBatchPreviewDialogAction({
  document,
  filePreview,
  successAction,
  isSentForSigning = false,
}: {
  document: DocumentItem;
  filePreview: (file: FileToPreview) => FilePreviewProps;
  successAction: DbotEvent;
  isSentForSigning?: boolean;
}): DocumentAction | null {
  if (!isSentForSigning) {
    return {
      type: "dbot/openSubscriptionFormBatchPreviewDialog",
      label: { key: "preview" },
      title: document.title,
      filePreview,
      successAction,
    };
  }
  return null;
}

const reviewStatuses = [
  DocumentSigningStatus.SENT_FOR_REVIEW,
  DocumentSigningStatus.SENT_FOR_VOTING,
];

const statusesAllowingDocumentToBeSentForSigning = [
  DocumentSigningStatus.UNKNOWN,
  ...reviewStatuses,
];

export function openSendForSigningDialogAction({
  document,
  showUnsupportedAttachmentWarning,
  signingStatus = DocumentSigningStatus.UNKNOWN,
  senderName,
}: {
  document: DocumentItem;
  signingStatus?: DocumentSigningStatus;
  showUnsupportedAttachmentWarning: boolean;
  senderName?: string;
}): DocumentAction | null {
  if (statusesAllowingDocumentToBeSentForSigning.includes(signingStatus)) {
    return {
      type: "dbot/validate-project-action",
      label: { key: "send-for-signing" },
      successAction: {
        type: "dbot/fetch-document-signers",
        documentId: document.documentId,
        onSuccess: (data: GetProjectDocumentSignersResponse) => [
          {
            type: "dbot/open-review-dialog",
            document,
            signers: data.signers,
            documents: data.documents,
            companies: data.companies,
            label: { key: "send-for-signing" },
            showUnsupportedAttachmentWarning,
            emailBody: {
              key: "dbot.send-for-signing.email-body",
              params: {
                senderName,
                fileName: document.title,
              },
            },
          },
        ],
        onFailure: [
          {
            type: "show-alert",
            messageType: "error",
            message: { key: "general-error" },
          },
        ],
      },
    };
  }
  return null;
}

export function openSigningStatusDialogAction(
  documentItem: DocumentItem,
  documentStatus?: ProjectDocumentStatus,
): DocumentAction | null {
  if (
    documentStatus?.signingStatus !== DocumentSigningStatus.UNKNOWN &&
    documentStatus?.signingStatus !== DocumentSigningStatus.SIGNING_COMPLETE
  ) {
    return {
      document: documentItem,
      type: "dbot/open-signing-status-dialog",
      label: {
        key: "n-of-m-have-signed",
        params: {
          n: documentStatus?.numberOfSignatures,
          m: documentStatus?.numberOfSigners,
        },
      },
    };
  }
  return null;
}

export function openSendNoticeDialogAction({
  document,
  sendingTime,
  isBatchProject,
}: {
  document: DocumentItem;
  sendingTime?: string;
  isBatchProject: boolean;
}): DocumentAction | null {
  if (!sendingTime && !isBatchProject) {
    return {
      type: "dbot/validate-project-action",
      label: { key: "dbot.send-notice" },
      successAction: {
        type: "dbot/open-review-dialog",
        document,
        label: document.title,
      },
    };
  }
  return null;
}

export function disabledSendNoticeInfo({
  isBatchProject,
}: {
  isBatchProject: boolean;
}): TMessage | undefined {
  return isBatchProject ? { key: "dbot.batchProject.disabled-send-notice-info" } : undefined;
}

export function openNotifyNoticeChangeDialogAction({
  document,
  sendingTime,
}: {
  document: DocumentItem;
  sendingTime?: string;
}): DocumentAction | null {
  if (sendingTime && document.type === DocumentType.notice_board_meeting) {
    return {
      type: "dbot/validate-project-action",
      label: { key: "dbot.send-notice.notify" },
      successAction: {
        type: "dbot/open-review-dialog",
        document,
        label: { key: "dbot.send-for-viewing.board-meeting.resend.title" },
        emailBody: { key: "dbot.send-for-viewing.board-meeting.resend.email-body" },
        isResend: true,
      },
    };
  }
  return null;
}

export function openViewingStatusDialog(
  documentItem: DocumentItem,
  documentStatus?: ProjectDocumentStatus,
): DocumentAction | null {
  if (documentStatus?.sentAt) {
    return {
      document: documentItem,
      type: "dbot/open-viewing-status-dialog",
      label: {
        key: "n-of-m-have-seen",
        params: {
          n: documentStatus?.numberOfViewed,
          m: documentStatus?.numberOfViewers,
        },
      },
    };
  }
  return null;
}

export class DecisionListItem {
  orgNo: string;
  decision: DecisionItem;
  followUps: FollowUp[];
  isApplying: boolean;
  isSendingToBR: boolean;
  isEnabled: boolean;
  isPreviousDecisionApplied: boolean;
  uploadedDecisionAttachments: DecisionAttachment[];
  fileUploadConfig: FileUploadConfig;
  documents?: DocumentListItem[];

  constructor(params: {
    orgNo: string;
    decision: DecisionItem;
    followUps: FollowUp[];
    isApplying: boolean;
    isSendingToBR: boolean;
    isEnabled: boolean;
    isPreviousDecisionApplied: boolean;
    uploadedDecisionAttachments: DecisionAttachment[];
    fileUploadConfig: FileUploadConfig;
    documents?: DocumentListItem[];
  }) {
    makeAutoObservable(this);
    this.orgNo = params.orgNo;
    this.decision = params.decision;
    this.followUps = params.followUps;
    this.isApplying = params.isApplying;
    this.isSendingToBR = params.isSendingToBR;
    this.isEnabled = params.isEnabled;
    this.isPreviousDecisionApplied = params.isPreviousDecisionApplied;
    this.uploadedDecisionAttachments = params.uploadedDecisionAttachments;
    this.fileUploadConfig = params.fileUploadConfig;
    this.documents = params.documents;
  }

  uploadNewDecisionAttachment(
    files: File[],
    dispatch: (action: DecisionAction) => void,
    attachmentType: AttachmentType,
    followUp: FollowUp,
  ) {
    if (files.length) {
      dispatch(
        uploadAttachmentsEvent({
          source: followUp,
          files,
          attachmentType,
          reference: this.decision.decisionId,
        }),
      );
    }
  }

  get applied() {
    return isDecisionApplied(this.followUps);
  }

  get supportsSendToBR() {
    return this.followUps.some((d) => d.data.type === FollowUpType.sendToBR);
  }

  get sendToBRFollowUpData(): SendToBRFollowUp | undefined {
    const sendToBR = this.followUps.find((d) => d.data.type === FollowUpType.sendToBR);
    return sendToBR ? (sendToBR.data as SendToBRFollowUp) : undefined;
  }

  get actions() {
    return {
      applyDecision: applyDecisionAction(this.decision),
      confirmSendToBR: confirmSendToBRAction(
        this.decision.decisionId,
        this.sendToBRFollowUpData?.brSigners ?? [],
      ),
    };
  }
}

export function unlockAction(documentId: string): DocumentAction {
  return {
    documentId,
    label: { key: "dbot.action.reopen" },
    type: "dbot/unlock-document",
  };
}

export function mapSignerToUpdateCompanyRoleItem(
  signer: Signer,
): UpdateCompanyRoleItem | undefined {
  const r = resolveUrnReference(signer.reference);
  switch (r?.type) {
    case "companyroleId":
      return { companyroleId: r.id, email: signer.email };
  }
}

export function hasInvalidRecipients(recipients: Recipient[]) {
  return !new RecipientsEditModel(recipients).validate();
}

export function getShareholderRecipients(shareholders: ExtendedShareholderDTO[]): Recipient[] {
  return shareholders
    .filter((s) => Object.values(s.calculatedShares).some((i) => i > 0))
    .map((s) => ({
      id: s.id,
      name: s.name,
      email: s.email,
    }));
}

export function getBoardRecipients(context: ProjectContext): Recipient[] {
  return context.boardMeetingNoticeRecipients.map((r) => ({ ...r, email: r.email ?? "" }));
}

export function getBoardRecipientsEligibleForPowerOfAttorney(context: ProjectContext) {
  const roleTypesEligibleForReceivingPowerOfAttorney: Set<RoleType> = new Set([
    RoleType.CHAIRPERSON,
    RoleType.CEO,
  ]);
  return getBoardRecipientsWithRoleTypes(context, roleTypesEligibleForReceivingPowerOfAttorney);
}

export function getBoardRecipientsWithRoleTypes(
  context: ProjectContext,
  roleTypes: Set<RoleType>,
): Recipient[] {
  const roles = context.companyInfo.roles;
  return roles
    .filter((r) => roleTypes.has(r.role))
    .map((r) => ({
      id: r.companyroleId,
      name: getFullName(r),
      email: r.email ?? "",
    }));
}

export function getBoardSignersInfo(context: ProjectContext): TMessage | undefined {
  if (!supportsSelectionOfSigners(context.companyInfo.roles)) {
    return { key: "dbot.boardmeeting.all-must-sign-when-less-than-5" };
  }
}

export function getViewingStatusLabel(s: DocumentViewingStatus): TMessage {
  if (s.viewedTime) {
    return { key: "dbot.summary.viewStatus.viewed" };
  } else if (
    s.emailStatus === EmailStatus.SENDING_FAILED ||
    s.emailStatus === EmailStatus.DELIVERY_FAILED
  ) {
    return { key: "dbot.summary.viewStatus.sendingFailed" };
  }
  return { key: "dbot.summary.viewStatus.waiting" };
}

export function getViewingStatusColor(s: DocumentViewingStatus): "success" | "error" | "neutral" {
  if (s.viewedTime) {
    return "success";
  } else if (
    s.emailStatus === EmailStatus.SENDING_FAILED ||
    s.emailStatus === EmailStatus.DELIVERY_FAILED
  ) {
    return "error";
  }
  return "neutral";
}

export function getSigningStatusColor(s: SigningStatusForSigner): "success" | "error" | "neutral" {
  if (s.isSigned) {
    return "success";
  }
  switch (s.sendingStatus) {
    case EmailStatus.DELIVERY_FAILED:
    case EmailStatus.SENDING_FAILED:
      return "error";
    default:
      return "neutral";
  }
}

export class ConfirmSendToBRDialogModel {
  readonly decisionId: string;
  onSubmit: (signer: BRSigner) => void;
  onCancel: () => void;
  brSignerModel: RadioFieldModel<BRSigner | undefined>;
  nameModel: StringFieldModel;
  emailModel: StringFieldModel;
  result?: Result<TMessage, TMessage>;

  constructor(
    decisionId: string,
    readonly brSigners: BRSigner[],
    onSubmit: (signer: BRSigner) => void,
    onCancel: () => void,
  ) {
    this.decisionId = decisionId;
    this.onSubmit = onSubmit;
    this.onCancel = onCancel;
    this.nameModel = new StringFieldModel();
    this.emailModel = new StringFieldModel();

    this.brSignerModel = new RadioFieldModel<BRSigner | undefined>({
      idFn: (option) => option?.role ?? "",
      labelFn: (option) =>
        option !== undefined
          ? option.role === BRSignerRole.other
            ? { key: "sendToBr.specify" }
            : { translated: option.name }
          : { translated: "" },
      options: brSigners,
      value: undefined,
      required: true,
      onChange: (value) => {
        this.nameModel.setRequired(value === undefined || isBlank(value.name));
        this.emailModel.setRequired(value === undefined || isBlank(value.email));
      },
    });
    makeAutoObservable(this);
  }

  handleEvent(event: DbotEvent) {
    switch (event.type) {
      case "dbot/send-to-br-result":
        this.result = event.result;
        break;
    }
  }

  get showEmailField(): boolean {
    if (this.brSignerModel.value === undefined) {
      return false;
    }
    return (
      this.brSignerModel.value?.role === BRSignerRole.other ||
      isBlank(this.brSignerModel.value?.email)
    );
  }

  get showName(): boolean {
    if (this.brSignerModel.value === undefined) {
      return false;
    }
    return (
      this.brSignerModel.value?.role === BRSignerRole.other ||
      isBlank(this.brSignerModel.value?.name)
    );
  }

  validateAndSubmit() {
    this.validate();
    if (this.isValid) {
      const data = this.prepareBRSigner();
      this.result = pending();
      this.onSubmit(data);
    }
  }

  validate() {
    this.brSignerModel.validate();
    this.nameModel.validate();
    this.emailModel.validate();
    return this.isValid;
  }

  get isValid(): boolean {
    if (!this.brSignerModel.isValid) {
      return false;
    }
    if (!this.nameModel.isValid) {
      return false;
    }
    return this.emailModel.isValid;
  }

  prepareBRSigner(): BRSigner {
    let data = this.brSignerModel.value!;
    data = {
      ...data,
      name: isBlank(data.name) ? this.nameModel.validatedValue : data.name,
      email: isBlank(data.email) ? this.emailModel.validatedValue : data.email,
    };
    return data;
  }
}

export function getSigningStatusLabel(item: SigningStatusForSigner): TMessage {
  if (item.isSigned) {
    return { key: "dbot.summary.signingStatus.signed" };
  }
  switch (item.sendingStatus) {
    case EmailStatus.UNSENT:
      return { key: "dbot.summary.signingStatus.not_sent" };
    case EmailStatus.SENT:
    case EmailStatus.DELIVERED:
      return { key: "dbot.summary.signingStatus.sent" };
    case EmailStatus.DELIVERY_FAILED:
      return { key: "dbot.summary.signingStatus.delivery_failed" };
    case EmailStatus.SENDING_FAILED:
      return { key: "dbot.summary.signingStatus.failed_to_send" };
    default:
      return { key: "dbot.summary.signingStatus.unknown" };
  }
}

export function getSendToBRPanel(item: DecisionListItem, followUp: FollowUp): DetailsPanel {
  return {
    type: "follow-up",
    id: "send-to-br",
    followUp: { item: followUp, decisionId: item.decision.decisionId },
    icon: !item.isEnabled
      ? "clock"
      : followUp.status === FollowUpStatus.IN_PROGRESS
        ? "arrows-clockwise"
        : followUp.status === FollowUpStatus.COMPLETED
          ? { name: "check", color: "success", weight: "bold" }
          : followUp.isActive
            ? { name: "arrow-right", color: "info" }
            : "clock",
    heading: { key: "dbot.summary.sendToBR" },
    label: !item.isEnabled
      ? { key: "dbot.summary.missing-system-settings" }
      : followUp.status === FollowUpStatus.IN_PROGRESS
        ? { key: "dbot.summary.inProgress" }
        : followUp.status === FollowUpStatus.COMPLETED
          ? undefined
          : !followUp.isActive
            ? { key: "dbot.summary.pending-decision" }
            : { key: "dbot.summary.not-completed" },
    hasValidationError: !item.isEnabled,
    notReady: !item.isEnabled || !followUp.isActive || followUp.status !== FollowUpStatus.READY,
    infoMessage: !item.isEnabled
      ? { key: "dbot.summary.disabledSendToBR" }
      : followUp.status === FollowUpStatus.IN_PROGRESS
        ? { key: "dbot.summary.isSentToBR" }
        : followUp.status === FollowUpStatus.COMPLETED
          ? undefined
          : getDecisionSendToBRDescription(item.decision.type),
  };
}

export function getApplyToSystemPanel(item: DecisionListItem, followUp: FollowUp): DetailsPanel {
  return {
    type: "follow-up",
    id: "apply-to-system",
    followUp: { item: followUp, decisionId: item.decision.decisionId },
    icon: !item.isEnabled
      ? "clock"
      : followUp.status === FollowUpStatus.COMPLETED
        ? { name: "check", color: "success", weight: "bold" }
        : followUp.isActive
          ? { name: "arrow-right", color: "info" }
          : "clock",
    heading: { key: "dbot.summary.applyDecisionToSystem" },
    hasValidationError: !item.isEnabled,
    label:
      followUp.status === FollowUpStatus.COMPLETED
        ? { key: "dbot.summary.completed" }
        : !item.isEnabled
          ? { key: "dbot.summary.missing-system-settings" }
          : !followUp.isActive
            ? { key: "dbot.summary.pending-decision" }
            : { key: "dbot.summary.not-completed" },
    notReady: !item.isEnabled || !followUp.isActive || followUp.status === FollowUpStatus.COMPLETED,
    infoMessage: !item.applied ? { key: "dbot.summary.applyDecisionToSystem.tooltip" } : undefined,
  };
}

export function getRequiredAttachmentsPanel(
  item: DecisionListItem,
  followUp: FollowUp,
): DetailsPanel {
  const data = followUp.data as AttachmentFollowUp;
  const uploadedAttachments = item.uploadedDecisionAttachments.filter(
    (a) => a.type === data.attachmentType,
  );
  return {
    type: "follow-up",
    id: data.attachmentType,
    followUp: {
      item: followUp,
      decisionId: item.decision.decisionId,
      attachmentType: data.attachmentType,
    },
    icon:
      followUp.status === FollowUpStatus.COMPLETED
        ? { name: "check", color: "success", weight: "bold" }
        : followUp.isActive
          ? { name: "arrow-right", color: "info" }
          : "clock",
    heading: requiredAttachmentLabel(data.attachmentType),
    hasValidationError: false,
    label:
      uploadedAttachments.length > 1
        ? { key: "dbot.summary.multipleFiles", params: { n: uploadedAttachments.length } }
        : uploadedAttachments.length > 0
          ? { key: "dbot.summary.completed" }
          : followUp.isActive
            ? { key: "dbot.summary.decisionRequiresDocumentation" }
            : { key: "dbot.summary.pending-decision" },
    action:
      uploadedAttachments.length === 1 && !isIdentificationAttachment(data.attachmentType)
        ? {
            label: { translated: uploadedAttachments[0].name },
            event: { type: "dbot/download-attachment", attachmentId: uploadedAttachments[0].id },
          }
        : undefined,
    notReady:
      followUp.isForInformation ||
      !followUp.isActive ||
      followUp.status === FollowUpStatus.COMPLETED,
    infoMessage:
      uploadedAttachments.length === 0
        ? getDecisionAttachmentsDescription(data.attachmentType)
        : undefined,
  };
}

export function getIncreaseOfShareCapitalSubscribersStatusPanel(
  item: DecisionListItem,
  followUp: FollowUp,
): DetailsPanel {
  const data = followUp.data as ViewSubscribersFollowUp;
  const isSubscriptionForm = item.documents?.some(
    (d) => d.document.type === DocumentType.subscription_form,
  );

  return {
    type: "follow-up",
    id: "view-subscribers",
    followUp: { item: followUp, decisionId: item.decision.decisionId },
    icon:
      followUp.status === FollowUpStatus.COMPLETED
        ? { name: "check", color: "success", weight: "bold" }
        : "user-list",
    heading: isSubscriptionForm
      ? { key: "dbot.summary.subscribersRights.heading" }
      : {
          key: `dbot.summary.subscribersStatus.heading`,
        },
    hasValidationError: false,
    label: getSubscribersStatusLabel(data.numberOfSubscribers),
    customOpenEvent: {
      type: "dbot/open-increase-of-share-capital-subscribers-status",
      decisionId: item.decision.decisionId,
      isLocked: followUp.status === FollowUpStatus.COMPLETED,
    },
    notReady:
      followUp.isForInformation ||
      !followUp.isActive ||
      followUp.status === FollowUpStatus.COMPLETED,
    infoMessage:
      followUp.status !== FollowUpStatus.COMPLETED
        ? { key: "dbot.summary.subscribersStatus.description.edit" }
        : undefined,
  };
}

export function getSubscribersStatusLabel(numberOfSubscribers: number): TMessage {
  return numberOfSubscribers === 0
    ? { key: "dbot.summary.subscribersStatus.label.noSubscribers" }
    : numberOfSubscribers === 1
      ? {
          key: "dbot.summary.subscribersStatus.label.subscriber",
          params: { numberOfSubscribers },
        }
      : {
          key: "dbot.summary.subscribersStatus.label.subscribers",
          params: { numberOfSubscribers },
        };
}

export function sendSubscriptionFormPanel(
  item: DecisionListItem,
  followUp: FollowUp,
  locale: Language,
): DetailsPanel {
  const data = followUp.data as SubscriptionFormFollowUp;
  const documentListItem = item.documents?.find((d) => d.document.documentId === data.documentId);

  return {
    type: "follow-up",
    id: "subscription-form",
    followUp: { item: followUp, decisionId: item.decision.decisionId },
    icon:
      followUp.status === FollowUpStatus.COMPLETED
        ? { name: "check", color: "success", weight: "bold" }
        : followUp.isActive
          ? { name: "arrow-right", color: "info" }
          : "clock",
    heading: getDocumentLabel(DocumentType.subscription_form),
    label: getSubscriptionFormStatusLabel(followUp, documentListItem),
    customOpenEvent:
      documentListItem === undefined
        ? undefined
        : followUp.status === FollowUpStatus.READY
          ? {
              type: "dbot/validate-project",
              onSuccess: {
                type: "dbot/open-review-dialog",
                document: documentListItem.document,
                label: { key: "send" },
              },
            }
          : {
              document: documentListItem?.document,
              type: "dbot/open-signing-status-dialog",
              label: { key: "show_status" },
            },
    notReady: !followUp.isActive || followUp.status === FollowUpStatus.COMPLETED,
    isDisabled: !followUp.isActive || documentListItem === undefined,
    infoMessage:
      documentListItem?.signingStatus === DocumentSigningStatus.SIGNING_COMPLETE
        ? undefined
        : followUp.status === FollowUpStatus.COMPLETED
          ? {
              key: "dbot.summary.subscriptionForm.description.sent",
              params: {
                timestamp: formatDate(locale, documentListItem?.sendingTime),
              },
            }
          : { key: "dbot.summary.subscriptionForm.description.sendToSubscribers" },
  };
}

export function getSubscriptionFormStatusLabel(
  followUp: FollowUp,
  documentListItem: DocumentListItem | undefined,
): TMessage {
  return documentListItem?.signingStatus === DocumentSigningStatus.SIGNING_COMPLETE
    ? { key: "dbot.summary.completed" }
    : followUp.status === FollowUpStatus.COMPLETED
      ? { key: "dbot.summary.signingStatus.sent" }
      : followUp.isActive
        ? { key: "dbot.summary.signingStatus.not_sent" }
        : { key: "dbot.summary.pending-decision" };
}

export function viewSubscriptionFormStatusPanel(
  item: DecisionListItem,
  followUp: FollowUp,
): DetailsPanel {
  const data = followUp.data as SubscriptionFormStatusFollowUp;
  const documentListItem = item.documents?.find((d) => d.document.documentId === data.documentId);

  return {
    type: "follow-up",
    id: "view-subscription-form-status",
    followUp: { item: followUp, decisionId: item.decision.decisionId },
    icon:
      documentListItem?.signingStatus === DocumentSigningStatus.SIGNING_COMPLETE
        ? { name: "check", color: "success", weight: "bold" }
        : followUp.isActive
          ? { name: "arrow-right", color: "info" }
          : "clock",
    heading: { key: "dbot.summary.subscribersStatus.heading" },
    customOpenEvent:
      documentListItem === undefined
        ? undefined
        : {
            document: documentListItem.document,
            label: { key: "show_status" },
            type: "dbot/open-subscription-status",
          },
    notReady: true,
    isDisabled: !followUp.isActive || documentListItem === undefined,
  };
}

export function increaseOfShareCapitalSubscribersAllocationPanel(
  item: DecisionListItem,
  followUp: FollowUp,
): DetailsPanel {
  return {
    type: "follow-up",
    id: "allocate-shares",
    followUp: { item: followUp, decisionId: item.decision.decisionId },
    icon:
      followUp.status === FollowUpStatus.COMPLETED
        ? { name: "check", color: "success", weight: "bold" }
        : followUp.isActive
          ? { name: "arrow-right", color: "info" }
          : "clock",
    heading: { key: "dbot.summary.subscribersAllocation.title" },
    label: getCommonFollowUpStatusLabel(followUp),
    customOpenEvent: {
      type: "dbot/open-increase-of-share-capital-subscribers-allocation",
      decisionId: item.decision.decisionId,
    },
    notReady: !followUp.isActive || followUp.status === FollowUpStatus.COMPLETED,
    infoMessage:
      followUp.status === FollowUpStatus.COMPLETED
        ? undefined
        : followUp.isActive
          ? { key: "dbot.summary.subscribersAllocation.description" }
          : { key: "dbot.summary.subscribersAllocation.description.disabled" },
  };
}

export function getCommonFollowUpStatusLabel(followUp: FollowUp): TMessage | undefined {
  switch (followUp.status) {
    case FollowUpStatus.IN_PROGRESS:
      return { key: "dbot.summary.inProgress" };
    case FollowUpStatus.COMPLETED:
      return undefined;
    default:
      return !followUp.isActive
        ? { key: "dbot.summary.pending-decision" }
        : { key: "dbot.summary.not-completed" };
  }
}

export function getDecisionFollowUpPanels(
  item: DecisionListItem,
  locale: Language,
): DetailsPanel[] {
  return item.followUps.map((followUp) => {
    switch (followUp.data.type) {
      case FollowUpType.viewSubscribers:
        return getIncreaseOfShareCapitalSubscribersStatusPanel(item, followUp);
      case FollowUpType.subscriptionForm:
        return sendSubscriptionFormPanel(item, followUp, locale);
      case FollowUpType.subscriptionFormStatus:
        return viewSubscriptionFormStatusPanel(item, followUp);
      case FollowUpType.allocatedShares:
        return increaseOfShareCapitalSubscribersAllocationPanel(item, followUp);
      case FollowUpType.attachment:
        return getRequiredAttachmentsPanel(item, followUp);
      case FollowUpType.identification:
        return getRequiredAttachmentsPanel(item, followUp);
      case FollowUpType.sendToBR:
        return getSendToBRPanel(item, followUp);
      case FollowUpType.apply:
        return getApplyToSystemPanel(item, followUp);
    }
  });
}

export function getActiveFollowUpPanels(items: DecisionListItem[], l: Language): number {
  return sumBy(
    items,
    (item) => getDecisionFollowUpPanels(item, l).filter((d) => !d.notReady).length,
  );
}

export function canBeSentForVoting(decisions: DecisionItem[], document: DocumentItem) {
  if (decisions.some((d) => d.decisionData?.type !== DecisionType.freetext)) {
    return true;
  } else {
    if (
      decisions.some(
        (d) =>
          d.decisionData?.type === DecisionType.freetext &&
          d.decisionData.freetextTypeForAllDocuments === FreetextType.DECISION_MATTER,
      )
    ) {
      return true;
    }

    return decisions.some(
      (d) =>
        d.decisionData?.type === DecisionType.freetext &&
        d.decisionData.freetextTypeForAllDocuments === FreetextType.CUSTOMIZED &&
        d.decisionData.customizedFreetextTypePerDocument.length > 0 &&
        d.decisionData.customizedFreetextTypePerDocument.some(
          (c) =>
            c.documentType === document.type &&
            c.customizedFreetextType === CustomizedFreetextType.DECISION_MATTER,
        ),
    );
  }
}

function generatePinCode() {
  return Math.floor(Math.random() * 9999)
    .toString()
    .padStart(4, "0");
}
